import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BellRinging, Plus, X } from "@phosphor-icons/react";
import { useApi, errText } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ReportSettings } from "@/components/alerts/ReportSettings";
import { EmailHistory } from "@/components/alerts/EmailHistory";
import { BoardTracking } from "@/components/alerts/BoardTracking";
import { useMe } from "@/components/app/TrialBanner";

const CHOICES = [1, 2, 3, 4, 5, 6, 9, 12, 18, 24];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const Recipients = ({ list, setList, founder, max, prefix = "recipient", noun = "co-founders", exclude = [] }) => {
  const [draft, setDraft] = useState("");
  const add = (e) => {
    e.preventDefault();
    const v = draft.trim().toLowerCase();
    if (!EMAIL_RE.test(v)) return toast.error("Enter a valid email address");
    if (v === founder?.toLowerCase() || exclude[0] === v) return toast.error(prefix === "investor" ? "You already get every board update" : "You already get these alerts");
    if (list.includes(v) || exclude.includes(v)) return toast.error("That address is already on a list");
    if (list.length >= max) return toast.error(`Up to ${max} ${noun}`);
    setList([...list, v]);
    setDraft("");
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {prefix === "recipient" && !founder && <span data-testid="recipient-founder-missing" className="text-xs text-[#A3A3A3]">Your account email couldn't be loaded right now; you'll still get alerts.</span>}
        {prefix === "recipient" && founder && <span data-testid="recipient-founder" className="rounded-full border border-[#00FF9D]/40 bg-[#00FF9D]/[0.06] px-3 py-1.5 text-xs font-mono">{founder} <span className="text-[#A3A3A3]">· you</span></span>}
        {list.map((r) => (
          <span key={r} data-testid={`${prefix}-${r}`} className="flex items-center gap-2 rounded-full border border-[#2A2A2A] px-3 py-1.5 text-xs font-mono">
            {r}
            <button data-testid={`${prefix}-remove-${r}`} onClick={() => setList(list.filter((x) => x !== r))} aria-label={`Remove ${r}`} className="text-[#6B6B6B] hover:text-[#FF5C7A] transition-colors"><X size={12} /></button>
          </span>
        ))}
      </div>
      <form onSubmit={add} noValidate className="mt-4 flex gap-2 max-w-md">
        <input data-testid={`${prefix}-input`} type="email" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={prefix === "investor" ? "investor@fund.com" : "cofounder@company.com"}
          className="flex-1 rounded-full bg-[#050505] border border-[#2A2A2A] px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#00FF9D] focus:outline-none placeholder:text-[#555]" />
        <button data-testid={`${prefix}-add-btn`} aria-disabled={list.length >= max}
          className={`flex items-center gap-1 rounded-full border border-[#2A2A2A] px-4 text-sm transition-colors ${list.length >= max ? "opacity-40 cursor-not-allowed" : "hover:border-[#00FF9D] hover:text-[#00FF9D]"}`}><Plus size={14} /> Add</button>
      </form>
      <p className="mt-2 text-[11px] text-[#A3A3A3]">{list.length}/{max} {noun} added</p>
    </div>
  );
};

export default function AlertSettings() {
  const role = useMe().data?.role;
  if (!role) return null;
  if (role !== "owner") return (
    <div data-testid="alert-settings-page" className="max-w-5xl">
      <p className="font-mono text-xs text-[#A3A3A3]">ALERTS & REPORTS</p>
      <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">Alerts & reports</h1>
      <p data-testid="alerts-owner-only" className="mt-6 rounded-xl border border-[#2A2A2A] bg-[#121212] p-6 text-sm text-[#A3A3A3]">Only the workspace owner can see and change alert recipients, investor lists and board reports.</p>
    </div>
  );
  return <OwnerAlertSettings />;
}

function OwnerAlertSettings() {
  const api = useApi();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["alert-settings"], queryFn: () => api.get("/alerts/settings") });
  const [threshold, setThreshold] = useState(6);
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) { setThreshold(data.threshold_months); setList(data.recipients); } }, [data]);

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.put("/alerts/settings", { threshold_months: threshold, recipients: list });
      qc.setQueryData(["alert-settings"], res);
      ["alert-status", "dashboard", "email-history"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success("Alert settings saved");
    } catch (e) {
      toast.error(e.response?.status === 422 ? "Check the email addresses" : errText(e, "Couldn't save settings"));
    }
    setBusy(false);
  };
  const dirty = data && (threshold !== data.threshold_months || JSON.stringify(list) !== JSON.stringify(data.recipients));

  return (
    <div data-testid="alert-settings-page" className="max-w-5xl">
      <p className="font-mono text-xs text-[#A3A3A3]">ALERTS & REPORTS</p>
      <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">Alerts & reports</h1>
      <p className="mt-3 text-sm text-[#A3A3A3]">We email you when your Zero Cash Date gets closer than your alert level, and again if it gets a month worse.</p>
      <section className="mt-8 rounded-xl border border-[#2A2A2A] bg-[#121212] p-6">
        <div className="flex items-center gap-2"><BellRinging className="text-[#FF5C7A]" /><h3 className="font-display text-lg">Alert level</h3></div>
        <p className="mt-1 text-sm text-[#A3A3A3]">Alert me when runway drops under</p>
        <Select value={String(threshold)} onValueChange={(v) => setThreshold(Number(v))}>
          <SelectTrigger data-testid="threshold-select" className="mt-3 w-48 rounded-full bg-[#050505] border-[#2A2A2A] font-mono"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#121212] border-[#2A2A2A]">
            {CHOICES.map((c) => <SelectItem key={c} value={String(c)} data-testid={`threshold-option-${c}`}>{c} {c === 1 ? "month" : "months"}</SelectItem>)}
          </SelectContent>
        </Select>
      </section>
      <section className="mt-4 rounded-xl border border-[#2A2A2A] bg-[#121212] p-6">
        <h3 className="font-display text-lg">Who gets the email</h3>
        <p className="mt-1 mb-4 text-sm text-[#A3A3A3]">You always get it. Add co-founders so they hear it at the same time.</p>
        {data ? <Recipients list={list} setList={setList} founder={data.founder_email} max={data.max_recipients} /> : <p className="text-sm text-[#A3A3A3]">Loading…</p>}
      </section>
      <button data-testid="alert-settings-save-btn" onClick={save} disabled={!dirty || busy}
        className="mt-6 rounded-full bg-[#00FF9D] px-8 py-3 font-semibold text-[#050505] hover:shadow-[0_0_24px_rgba(0,255,157,0.5)] transition-shadow disabled:opacity-40">
        {busy ? "Saving…" : "Save alert settings"}
      </button>
      <ReportSettings founder={data?.founder_email} cofounders={data?.recipients || []} />
      <BoardTracking />
      <EmailHistory />
    </div>
  );
}
